import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Tag,
  message,
  Row,
  Col,
} from "antd";
import dayjs from "dayjs";
import "./AdminTools.css";
import { useAuth } from "../AuthProvider";
import APIService from "../../Service/APIService";
import Table from "../../common/table";
import TableOptionsCell from "../../common/table/table-options-cell";

const DATE_TIME_FORMAT = "MM-DD-YYYY hh:mm A";

const BannerAlerts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiServiceRef = React.useRef(new APIService());
  const [form] = Form.useForm();

  const [bannerAlerts, setBannerAlerts] = React.useState([]);
  const [isBannerAlertsLoading, setIsBannerAlertsLoading] = React.useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = React.useState(false);
  const [isSavingAlert, setIsSavingAlert] = React.useState(false);
  const [editingAlert, setEditingAlert] = React.useState(null);

  const getStatusColor = (status) => {
    if (status === "Active") return "green";
    if (status === "Schedule") return "orange";
    return "default";
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : "-";
  };

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback;

  const fetchBannerAlerts = React.useCallback(async () => {
    setIsBannerAlertsLoading(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        "getbanneralerts"
      );
      setBannerAlerts(response?.data?.data || []);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load banner alerts"));
    } finally {
      setIsBannerAlertsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const userData = user;
    if (!userData) return;

    if (userData.Type !== "Admin" || !userData.IsSuperAdmin) {
      navigate("/");
      return;
    }

    fetchBannerAlerts();
  }, [fetchBannerAlerts, navigate, user]);

  const closeAlertModal = () => {
    setIsAlertModalOpen(false);
    setEditingAlert(null);
    form.resetFields();
  };

  const openCreateAlertModal = () => {
    setEditingAlert(null);
    form.resetFields();
    setIsAlertModalOpen(true);
  };

  const openEditAlertModal = (alert) => {
    setEditingAlert(alert);
    form.setFieldsValue({
      AlertName: alert.AlertName,
      AlertDescription: alert.AlertDescription,
      StartDate: alert.StartDate ? dayjs(alert.StartDate) : null,
      EndDate: alert.EndDate ? dayjs(alert.EndDate) : null,
    });
    setIsAlertModalOpen(true);
  };

  const handleSaveAlert = async () => {
    try {
      const values = await form.validateFields();
      setIsSavingAlert(true);

      const payload = {
        AlertName: values.AlertName.trim(),
        AlertDescription: values.AlertDescription.trim(),
        StartDate: values.StartDate.toISOString(),
        EndDate: values.EndDate.toISOString(),
      };

      if (editingAlert) {
        await apiServiceRef.current.updateDataToBackend(
          { ...payload, ID: editingAlert.ID },
          "updatebanneralert"
        );
        message.success("Banner alert updated successfully");
      } else {
        await apiServiceRef.current.addDataToBackend(payload, "addbanneralert");
        message.success("Banner alert created successfully");
      }

      closeAlertModal();
      await fetchBannerAlerts();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getErrorMessage(error, "Unable to save banner alert"));
    } finally {
      setIsSavingAlert(false);
    }
  };

  const bannerAlertColumns = [
    {
      headerName: "Alert Name",
      field: "AlertName",
      filter: true,
      minWidth: 220,
    },
    {
      headerName: "Alert Description",
      field: "AlertDescription",
      filter: true,
      minWidth: 320,
    },
    {
      headerName: "Start Date",
      field: "StartDate",
      filter: true,
      minWidth: 180,
      cellRenderer: (params) => formatDateTime(params.data?.StartDate),
    },
    {
      headerName: "End Date",
      field: "EndDate",
      filter: true,
      minWidth: 180,
      cellRenderer: (params) => formatDateTime(params.data?.EndDate),
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      minWidth: 140,
      cellRenderer: (params) => (
        <Tag color={getStatusColor(params.data?.Status)}>
          {params.data?.Status || "-"}
        </Tag>
      ),
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      filter: false,
      minWidth: 160,
      cellRenderer: (params) => (
        <TableOptionsCell
          optionOne="Edit Alert"
          optionOneType="warning"
          optionOneClickFn={() => openEditAlertModal(params.data)}
          rowData={params.data}
        />
      ),
    },
  ];

  return (
    <div className="admin-tools-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="text text-h1">Banner Alerts</div>
          <div className="text text-smallText admin-tools-subtitle">
            Manage all scheduled and active banner alerts from one place.
          </div>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/AdminTools")}>
          Back to Admin Tools
        </Button>
      </div>

      <div className="admin-tools-row">
        <div className="admin-tools-banner-alerts">
          <div className="admin-tools-banner-alerts-header">
            <div>
              <div className="text text-h2">All Banner Alerts</div>
              <div className="text text-smallText admin-tools-subtitle">
                Create, review, and edit banner alerts displayed in the top bar.
              </div>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateAlertModal}>
              New Alert
            </Button>
          </div>

          <Table
            rowData={bannerAlerts}
            columnDefs={bannerAlertColumns}
            loading={isBannerAlertsLoading}
          />
        </div>
      </div>

      <Modal
        title={editingAlert ? "Edit Alert" : "Create New Alert"}
        open={isAlertModalOpen}
        onCancel={closeAlertModal}
        onOk={handleSaveAlert}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isSavingAlert}
        destroyOnClose
        width={400}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Alert Name"
                name="AlertName"
                rules={[
                  { required: true, message: "Alert Name is required" },
                  {
                    max: 200,
                    message: "Alert Name cannot exceed 200 characters",
                  },
                ]}
              >
                <Input maxLength={200} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Date"
                name="StartDate"
                rules={[{ required: true, message: "Start Date is required" }]}
              >
                <DatePicker
                  showTime
                  format={DATE_TIME_FORMAT}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="End Date"
                name="EndDate"
                dependencies={["StartDate"]}
                rules={[
                  { required: true, message: "End Date is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("StartDate");
                      if (!value || !startDate || value.isAfter(startDate)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("End Date must be after Start Date")
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  showTime
                  format={DATE_TIME_FORMAT}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Alert Description"
                name="AlertDescription"
                rules={[
                  { required: true, message: "Alert Description is required" },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default BannerAlerts;
